let draftSequence = 0;
export const createAccountingId = () => `draft-${Date.now()}-${++draftSequence}`;

export function parseAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (value == null || value === '') return 0;
  const text = String(value).trim().replace(/^(?:NT\$|\$)\s*/, '').replace(/,/g, '');
  return /^-?\d+(?:\.\d+)?$/.test(text) ? Number(text) : NaN;
}
export const formatAmount = (value) => {
  const number = parseAmount(value);
  return Number.isFinite(number) ? new Intl.NumberFormat('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(number) : '—';
};

const validLocalDate = (year, month, day, hour = 0, minute = 0) => {
  const date = new Date(year, month - 1, day, hour, minute);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day && date.getHours() === hour && date.getMinutes() === minute ? date : null;
};
export function parseAccountingDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  const legacy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (legacy) {
    const [, month, day, year, hour, minute, period] = legacy;
    if (+hour < 1 || +hour > 12) return null;
    return validLocalDate(+year, +month, +day, +hour % 12 + (period.toUpperCase() === 'PM' ? 12 : 0), +minute);
  }
  const local = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/);
  if (local) return +(local[6] || 0) > 59 ? null : validLocalDate(+local[1], +local[2], +local[3], +(local[4] || 0), +(local[5] || 0));
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.test(text)) return null;
  const calendar = text.slice(0, 10).split('-').map(Number);
  if (!validLocalDate(calendar[0], calendar[1], calendar[2])) return null;
  const date = new Date(text);
  return Number.isFinite(date.getTime()) ? date : null;
}
const pad = (value) => String(value).padStart(2, '0');
export function toLocalDateTime(value) {
  const date = value instanceof Date ? value : parseAccountingDate(value);
  if (!date) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
export const formatAccountingDate = (value) => {
  const date = parseAccountingDate(value);
  return date ? new Intl.DateTimeFormat('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(date) : '未提供日期';
};
export function normalizeAccountingData(data) {
  return {
    debt: (data?.debt || []).map((row, index) => ({ ...row, id: row.id ?? `debt-${index}`, name: row.name || '', transaction_amount: Number.isFinite(parseAmount(row.transaction_amount)) ? parseAmount(row.transaction_amount) : row.transaction_amount, order_quantity: row.order_quantity ?? 0, sum_price: Number.isFinite(parseAmount(row.sum_price)) ? parseAmount(row.sum_price) : row.sum_price })),
    payments: (data?.payments || []).map((row, index) => ({ ...row, id: row.id ?? `payment-${index}`, name: row.name || '', payment_amount: Number.isFinite(parseAmount(row.payment_amount)) ? parseAmount(row.payment_amount) : row.payment_amount, timestamp: row.timestamp || row.time || '', notes: row.notes || '' })),
  };
}
export function validateAccountingData(data) {
  for (const [type, rows] of Object.entries(data)) {
    for (const [index, row] of rows.entries()) {
      const prefix = `${type === 'debt' ? '帳單' : '付款'}第 ${index + 1} 筆`;
      if (!row.name?.trim()) return `${prefix}：請填寫名稱。`;
      if (row.name.trim().length > 255) return `${prefix}：名稱請勿超過 255 字。`;
      const amounts = type === 'debt' ? [row.transaction_amount, row.sum_price] : [row.payment_amount];
      if (amounts.some((value) => value === '' || !Number.isFinite(parseAmount(value)) || parseAmount(value) < 0 || parseAmount(value) > 9999999999.99 || Math.abs(parseAmount(value) * 100 - Math.round(parseAmount(value) * 100)) > 0.0001)) return `${prefix}：金額須為 0 以上、最多兩位小數的有效數字。`;
      if (type === 'debt' && (row.order_quantity === '' || !Number.isInteger(Number(row.order_quantity)) || +row.order_quantity < 0 || +row.order_quantity > 2147483647)) return `${prefix}：數量須為 0 以上的整數。`;
      if (type === 'payments' && !parseAccountingDate(row.timestamp)) return `${prefix}：請填寫有效的付款日期與時間。`;
    }
  }
  return '';
}
export const EMPTY_BILL_FILTERS = { keyword: '', type: 'all', from: '', to: '', min: '', max: '', sort: 'original' };
export function getBillFilterError(filters) {
  if (filters.from && filters.to && filters.from > filters.to) return '開始日期不可晚於結束日期。';
  if ([filters.min, filters.max].some(value => value !== '' && (!Number.isFinite(parseAmount(value)) || parseAmount(value) < 0))) return '查詢金額請輸入 0 以上的數字。';
  if (filters.min !== '' && filters.max !== '' && parseAmount(filters.min) > parseAmount(filters.max)) return '最低金額不可高於最高金額。';
  return '';
}
export function queryBills(data, filters = EMPTY_BILL_FILTERS) {
  if (getBillFilterError(filters)) return [];
  const keyword = filters.keyword.trim().toLocaleLowerCase();
  const rows = [
    ...(data.debt || []).map(row => ({ ...row, type: 'debt', amount: parseAmount(row.sum_price), date: '' })),
    ...(data.payments || []).map(row => ({ ...row, type: 'payment', amount: parseAmount(row.payment_amount), date: row.timestamp || row.time || '' })),
  ].filter(row => {
    if (filters.type !== 'all' && row.type !== filters.type) return false;
    if (keyword && !`${row.name || ''} ${row.notes || ''}`.toLocaleLowerCase().includes(keyword)) return false;
    if (filters.min !== '' && (!Number.isFinite(row.amount) || row.amount < parseAmount(filters.min))) return false;
    if (filters.max !== '' && (!Number.isFinite(row.amount) || row.amount > parseAmount(filters.max))) return false;
    if (filters.from || filters.to) {
      const day = toLocalDateTime(row.date).slice(0, 10);
      if (!day || (filters.from && day < filters.from) || (filters.to && day > filters.to)) return false;
    }
    return true;
  });
  return rows.sort((a, b) => {
    if (filters.sort === 'amount-desc') return b.amount - a.amount;
    if (filters.sort === 'amount-asc') return a.amount - b.amount;
    if (filters.sort === 'name') return a.name.localeCompare(b.name, 'zh-TW');
    if (filters.sort === 'date-desc') return (parseAccountingDate(b.date)?.getTime() || 0) - (parseAccountingDate(a.date)?.getTime() || 0);
    return 0;
  });
}
